/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2024, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import { AnyAction, createSlice } from '@reduxjs/toolkit';

import { ApiStatus } from '../../../ts';
import { SEARCH_AUTH_TYPES } from '../../../ts/skill';
import type { MilvusServiceSlice } from '../../../ts/state';
import {
  CONFIDENCE_THRESHOLD_VALUE,
  RESPONSE_LENGTH_VALUE
} from '../../discovery/util/constants';
import {
  checkCreds,
  getFields,
  getIndices,
  getModels,
  listCollections,
  listDataSources
} from './actions';
import { milvusAdapter } from './adapter';

const initialState: MilvusServiceSlice = milvusAdapter.getInitialState({
  draft: null,
  isDirty: false,
  isEditing: false,
  authType: SEARCH_AUTH_TYPES.BASIC,
  isSetupValid: false,
  isConfigValid: false,
  create: false,
  createStatus: ApiStatus.IDLE,
  createError: null,
  skillId: null,
  checkCredsError: null,
  checkCredsStatus: ApiStatus.IDLE,
  dataSourcesStatus: ApiStatus.IDLE,
  dataSourcesError: null,
  dataSources: null,
  collectionsStatus: ApiStatus.IDLE,
  collectionsError: null,
  collections: null,
  fieldsStatus: ApiStatus.IDLE,
  fieldsError: null,
  fields: null,
  getIndicesStatus: ApiStatus.IDLE,
  getIndicesError: null,
  indices: null,
  getModelsStatus: ApiStatus.IDLE,
  getModelsError: null,
  models: []
});

// Slice & Reducer
const slice = createSlice({
  name: 'milvus',
  initialState,
  reducers: {
    resetState() {
      return initialState;
    },
    setUrl(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.url = action.payload;
      state.isDirty = true;
    },
    setPort(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.port = action.payload;
      state.isDirty = true;
    },
    setUser(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.username = action.payload;
      state.isDirty = true;
    },
    clearUser(state: MilvusServiceSlice) {
      delete state?.draft?.search_settings?.milvus?.username;
    },
    setPass(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.password = action.payload;
      state.isDirty = true;
    },
    clearPass(state: MilvusServiceSlice) {
      delete state?.draft?.search_settings?.milvus?.password;
    },
    setDatabase(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.database = action.payload;
      state.isDirty = true;
    },
    setCollection(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.collection = action.payload;
      state.isDirty = true;
    },
    setIndex(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.index = action.payload;
      state.isDirty = true;
    },
    clearIndex(state: MilvusServiceSlice) {
      delete state.draft.search_settings.milvus.index;
    },
    clearCollection(state: MilvusServiceSlice) {
      delete state.draft.search_settings.milvus.collection;
    },
    setModelId(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.embeddings_model_id = action.payload;
      state.isDirty = true;
    },
    setNoAuth(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.no_auth = action.payload;
      state.isDirty = true;
    },
    setAuthType(state: MilvusServiceSlice, action) {
      state.authType = action.payload;
    },
    setAuthTypeDefault(state: MilvusServiceSlice) {
      state.authType = SEARCH_AUTH_TYPES.BASIC;
    },
    clearDiscoveryDetails(state: MilvusServiceSlice) {
      delete state?.draft?.search_settings?.discovery;
    },
    setIsSetupValid(state: MilvusServiceSlice, action) {
      state.isSetupValid = action.payload;
    },
    setIsConfigValid(state: MilvusServiceSlice, action) {
      state.isConfigValid = action.payload;
    },
    setSearchSkillDraft(state: MilvusServiceSlice, action) {
      const { skill, isDirty } = action.payload;

      state.draft = skill || null;
      state.isDirty = isDirty;
    },
    setIsDirty(state: MilvusServiceSlice, action) {
      state.isDirty = action.payload;
    },
    resetIsDirty(state: MilvusServiceSlice) {
      state.isDirty = false;
    },
    setIsEditing(state: MilvusServiceSlice, action) {
      state.isEditing = action.payload;
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftConversationalSearchEnabledDefault(state: MilvusServiceSlice) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: false
      };
    },
    setDraftConversationalSearchRetrievalConfidenceThresholdDefault(
      state: MilvusServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchConfidenceThresholdDefault(
      state: MilvusServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    setDraftConversationalSearchVerbosityOptionDefault(
      state: MilvusServiceSlice
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: RESPONSE_LENGTH_VALUE.MODERATE
      };
    },
    setDraftConversationalSearchResponseConfidenceThresholdDefault(
      state: MilvusServiceSlice
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: CONFIDENCE_THRESHOLD_VALUE.LESS_OFTEN
      };
    },
    // This reducer does not set isDirty to true because it's just updating defaults. We don't need to confuse tanya by
    // enabling the save button for the defaults, instead the defaults will only be saved if she makes an edit and then
    // chooses to save.
    setDraftCitationsTitleValueDefault(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
    },
    setDraftMaxCitationsValueDefault(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
    },
    setDraftIsConversationalSearchEnabled(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchRetrievalConfidenceThreshold(
      state: MilvusServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        pre_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchConfidenceThreshold(
      state: MilvusServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchResponseConfidenceThreshold(
      state: MilvusServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.search_confidence = {
        ...state.draft.search_settings.conversational_search.search_confidence,
        post_gen_threshold: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurn(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: action.payload
      };
      state.isDirty = true;
    },
    setDraftConversationalSearchMultiTurnDefault(state: MilvusServiceSlice) {
      state.draft.search_settings.conversational_search.multi_turn = {
        ...state.draft.search_settings.conversational_search.multi_turn,
        enabled: false
      };
    },
    setDraftConversationalSearchVerbosityOption(
      state: MilvusServiceSlice,
      action
    ) {
      state.draft.search_settings.conversational_search.response_length = {
        ...state.draft.search_settings.conversational_search.response_length,
        option: action.payload
      };
      state.isDirty = true;
    },
    setDraftCitationsTitleValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search = {
        ...state.draft.search_settings.conversational_search,
        citations_title: action.payload
      };
      state.isDirty = true;
    },
    setDraftMaxCitationsValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.conversational_search.citations = {
        ...state.draft.search_settings.conversational_search.citations,
        max_citations: action.payload
      };
      state.isDirty = true;
    },
    setDraftSuccessMessage(state: MilvusServiceSlice, action) {
      state.draft.search_settings.messages.success = action.payload;
      state.isDirty = true;
    },
    setDraftNoResultsMessage(state: MilvusServiceSlice, action) {
      state.draft.search_settings.messages.no_result = action.payload;
      state.isDirty = true;
    },
    setDraftConnectivityMessage(state: MilvusServiceSlice, action) {
      state.draft.search_settings.messages.error = action.payload;
      state.isDirty = true;
    },
    setDraftTitleOptionValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.schema_mapping.title = action.payload;
      state.isDirty = true;
    },
    setDraftBodyOptionValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.schema_mapping.body = action.payload;
      state.isDirty = true;
    },
    setDraftUrlOptionValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.schema_mapping.url = action.payload;
      state.isDirty = true;
    },
    setDraftFilterValue(state: MilvusServiceSlice, action) {
      state.draft.search_settings.milvus.filter = action.payload;
      state.isDirty = true;
    }
  },
  extraReducers: builder => {
    builder
      .addCase(checkCreds.pending, (state: MilvusServiceSlice) => {
        state.checkCredsStatus = ApiStatus.LOADING;
        state.checkCredsError = null;
      })
      .addCase(checkCreds.fulfilled, (state: MilvusServiceSlice) => {
        state.checkCredsStatus = ApiStatus.SUCCEEDED;
        state.checkCredsError = null;
      })
      .addCase(
        checkCreds.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.checkCredsStatus = ApiStatus.ERROR;
          state.checkCredsError = action.error;
        }
      )
      .addCase(listDataSources.pending, (state: MilvusServiceSlice) => {
        state.dataSourcesStatus = ApiStatus.LOADING;
        state.dataSourcesError = null;
      })
      .addCase(
        listDataSources.fulfilled,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.dataSourcesStatus = ApiStatus.SUCCEEDED;
          state.dataSourcesError = null;
          state.dataSources = action.payload;
        }
      )
      .addCase(
        listDataSources.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.dataSourcesStatus = ApiStatus.ERROR;
          state.dataSourcesError = action.error;
        }
      )
      .addCase(listCollections.pending, (state: MilvusServiceSlice) => {
        state.collectionsStatus = ApiStatus.LOADING;
        state.collectionsError = null;
      })
      .addCase(
        listCollections.fulfilled,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.collectionsStatus = ApiStatus.SUCCEEDED;
          state.collectionsError = null;
          state.collections = action.payload;
        }
      )
      .addCase(
        listCollections.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.collectionsStatus = ApiStatus.ERROR;
          state.collectionsError = action.error;
        }
      )
      .addCase(getIndices.pending, (state: MilvusServiceSlice) => {
        state.getIndicesStatus = ApiStatus.LOADING;
        state.getIndicesError = null;
      })
      .addCase(
        getIndices.fulfilled,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.getIndicesStatus = ApiStatus.SUCCEEDED;
          state.getIndicesError = null;
          state.indices = action.payload;
        }
      )
      .addCase(
        getIndices.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.getIndicesStatus = ApiStatus.ERROR;
          state.getIndicesError = action.error;
        }
      )
      .addCase(getFields.pending, (state: MilvusServiceSlice) => {
        state.fieldsStatus = ApiStatus.LOADING;
        state.fieldsError = null;
      })
      .addCase(
        getFields.fulfilled,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.fieldsStatus = ApiStatus.SUCCEEDED;
          state.fieldsError = null;
          state.fields = action.payload;
        }
      )
      .addCase(
        getFields.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.fieldsStatus = ApiStatus.ERROR;
          state.fieldsError = action.error;
        }
      )
      .addCase(getModels.pending, (state: MilvusServiceSlice) => {
        state.getModelsStatus = ApiStatus.LOADING;
        state.getModelsError = null;
      })
      .addCase(
        getModels.fulfilled,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.getModelsStatus = ApiStatus.SUCCEEDED;
          state.getModelsError = null;
          state.models = action.payload;
        }
      )
      .addCase(
        getModels.rejected,
        (state: MilvusServiceSlice, action: AnyAction) => {
          state.getModelsStatus = ApiStatus.ERROR;
          state.getModelsError = action.error;
        }
      );
  }
});

export const {
  resetState,
  setUrl,
  setPort,
  setUser,
  clearUser,
  setPass,
  clearPass,
  setDatabase,
  setCollection,
  clearCollection,
  setIndex,
  clearIndex,
  setModelId,
  setNoAuth,
  setAuthType,
  setAuthTypeDefault,
  clearDiscoveryDetails,
  setIsSetupValid,
  setIsConfigValid,
  setSearchSkillDraft,
  setIsDirty,
  resetIsDirty,
  setIsEditing,
  setDraftConversationalSearchEnabledDefault,
  setDraftConversationalSearchRetrievalConfidenceThresholdDefault,
  setDraftConversationalSearchConfidenceThresholdDefault,
  setDraftConversationalSearchVerbosityOptionDefault,
  setDraftConversationalSearchResponseConfidenceThresholdDefault,
  setDraftCitationsTitleValueDefault,
  setDraftMaxCitationsValueDefault,
  setDraftIsConversationalSearchEnabled,
  setDraftConversationalSearchRetrievalConfidenceThreshold,
  setDraftConversationalSearchConfidenceThreshold,
  setDraftConversationalSearchVerbosityOption,
  setDraftConversationalSearchResponseConfidenceThreshold,
  setDraftTitleOptionValue,
  setDraftBodyOptionValue,
  setDraftUrlOptionValue,
  setDraftCitationsTitleValue,
  setDraftMaxCitationsValue,
  setDraftSuccessMessage,
  setDraftNoResultsMessage,
  setDraftConnectivityMessage,
  setDraftFilterValue,
  setDraftConversationalSearchMultiTurn,
  setDraftConversationalSearchMultiTurnDefault
} = slice.actions;

export default slice.reducer;
